import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Subscription } from 'rxjs';

import { UNLIMITED_PASSES } from '@fantaz/shared/config';
import {
  actionTypes,
  columnTypes,
  IColumnModel,
  IElementModel,
  ITableConfig,
  MatchResultsForUser,
  RoomFilter,
  RoomFilterModel,
} from '@fantaz/shared/data-access/models';
import { PlacePipe } from '@fantaz/shared/pipes';
import { CurrencyService } from '@fantaz/shared/utils';

@Component({
  selector: 'fz-history-list-table',
  templateUrl: './history-list-table.component.html',
  styleUrls: ['./history-list-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryListTableComponent
  implements OnInit, OnDestroy, DoCheck, OnChanges
{
  /**
   * Config to disable/enable the filter
   */
  @Input()
  disableFilter = false;

  @Input()
  loading: boolean | null;

  @Input()
  gameHistory: MatchResultsForUser[] | null;

  gameHistoryFiltered: MatchResultsForUser[];

  @Input()
  totalGameHistory: number | null;

  @Input()
  loggedIn: boolean | null;

  @Input()
  emptyActionText: string | null;

  @Input()
  tryFreeEnabled = false;

  @Output()
  pageChange: EventEmitter<any> = new EventEmitter();

  @Output()
  viewPlayInstance: EventEmitter<MatchResultsForUser> =
    new EventEmitter<MatchResultsForUser>();

  @Output()
  tryFree: EventEmitter<Event> = new EventEmitter<Event>();

  @Output()
  login: EventEmitter<Event> = new EventEmitter<Event>();

  @Input()
  navigateOutside: boolean;

  @Output()
  navigateOutsideEvent: EventEmitter<string> = new EventEmitter();

  #currencyService: CurrencyService = inject(CurrencyService);

  @Input()
  level = 'global';

  @Input()
  page = {
    pageIndex: 0,
    pageSize: 10,
  };

  showFilter = false;
  currentScreenWidth = '';
  flexMediaWatcher: Subscription;
  filterHistoryModel: RoomFilterModel[] = [
    {
      filter: 'entryFee',
      label: 'Tournament Entry Fee',
      symbol: '$',
    },
    {
      filter: 'awardedPrizeAmount',
      label: 'Prize',
      symbol: '$',
    },
  ];

  displayedColumns: IColumnModel[] = [];

  configuration: ITableConfig = {
    headersTooltip: {},
    tableName: `${this.level}-game-history`,
    pagination: {
      indexQueryParam: 'historyIndex',
      sizeQueryParam: 'historySize',
      paginationSizeOptions: [10, 50, 100],
    },
    emptyState: {
      title: ' ',
    },
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalGameHistory'] && this.configuration.pagination) {
      this.configuration.pagination.paginationTotal = this.totalGameHistory;
      this.configuration.pagination.showPagination =
        (!this.navigateOutside &&
          this.totalGameHistory &&
          this.totalGameHistory > 5) ||
        false;
    }
  }
  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private cd: ChangeDetectorRef,
  ) {}

  setupTable() {
    let columns: string[] = ['entryFee', 'score', 'place', 'rating', 'actions'];
    if (this.currentScreenWidth === 'sm') {
      // only display this fields on medium screens
      if (this.level == 'global') {
        columns.unshift('game', 'event', 'event');
      } else if (this.level == 'tournament') {
        columns.unshift('event');
      }
    } else {
      // display al fields on larger screens
      columns = ['entryFee', 'score', 'place', 'playedOn', 'actions'];
      if (this.level == 'global') {
        columns.unshift('game', 'event');
      } else if (this.level == 'tournament') {
        columns.unshift('event');
      }
    }

    for (let i = 0; i < columns.length; i++) {
      if (
        columns[i] === 'event' &&
        this.displayedColumns.findIndex((col) => col.id === 'event') === -1
      ) {
        this.displayedColumns.push({
          id: 'event',
          title: 'Tournament',
          type: () => columnTypes.EVENT,
          mapping: (element: MatchResultsForUser) => {
            const result = this.getRoomNumber(element);
            return result;
          },
          tooltip: () => {
            return 'View Tournament Details';
          },
        });
      } else if (
        columns[i] === 'game' &&
        this.displayedColumns.findIndex((col) => col.id === 'game') === -1
      ) {
        this.displayedColumns.push({
          id: 'game',
          title: 'Game',
          type: () => columnTypes.IMG,
          mapping: (element: MatchResultsForUser) =>
            this.getGameImage(element.gameKey),
          tooltip: () => null,
        });
      } else if (
        columns[i] === 'event' &&
        this.displayedColumns.findIndex((col) => col.id === 'event') === -1
      ) {
        this.displayedColumns.push({
          id: 'event',
          title: 'Tournament',
          type: () => columnTypes.DEFAULT,
          mapping: (element: MatchResultsForUser) => element.tournamentName,
          tooltip: (element: MatchResultsForUser) => {
            if (element) {
              return 'View Tournament Details';
            } else return null;
          },
        });
      } else if (
        columns[i] === 'entryFee' &&
        this.displayedColumns.findIndex((col) => col.id === 'entryFee') === -1
      ) {
        this.displayedColumns.push({
          id: 'entryFee',
          title: 'Entry',
          type: () => columnTypes.CUSTOM,
          mapping: (element: MatchResultsForUser) =>
            element.fantazPass ? element.fantazPass.name : element.entryFee,
          customElement: (element: MatchResultsForUser) => {
            let value = '';
            if (element.fantazPass) {
              const price = this.#currencyService.convertDollar(
                Number(element.fantazPass.price.price),
              );
              const promo =
                element.fantazPass?.price &&
                element.fantazPass.price.promoCodes &&
                element.fantazPass.price?.promoCodes?.length &&
                element.fantazPass.price.promoCodes[0]
                  ? element.fantazPass.price.promoCodes[0].uuid
                  : '';
              const entry =
                Number(element.fantazPass.entries) -
                element.fantazPass.activeCount;
              const entries = element.fantazPass.entries;
              const entriesElm =
                entries < UNLIMITED_PASSES
                  ? ` <span>(${entry}/${element.fantazPass.entries})</span>`
                  : `<span>(Unlimited)</span>`;
              value = `<span>${element.fantazPass.name}</span>
              <span>$${price}</span> <span class="code">USD</span>
               ${entriesElm}
               <span>${promo}</span>
             `;
            } else {
              const entryFee = this.#currencyService.convertDollar(
                Number(element.entryFee),
              );
              value = `<span class="prize-ctn">
            <span class="value">${entryFee}</span
            > <span class="code">USD</span>
          </span>`;
            }
            return value;
          },
          tooltip: () => null,
        });
      } else if (
        columns[i] === 'score' &&
        this.displayedColumns.findIndex((col) => col.id === 'score') === -1
      ) {
        this.displayedColumns.push({
          id: 'score',
          title: 'Score',
          type: () => columnTypes.DEFAULT,
          mapping: (element: MatchResultsForUser) => element.finalScore,
          tooltip: () => null,
        });
      } else if (
        columns[i] === 'place' &&
        this.displayedColumns.findIndex((col) => col.id === 'place') === -1
      ) {
        this.displayedColumns.push({
          id: 'place',
          title: 'Place',
          type: () => columnTypes.CUSTOM,
          mapping: (element: MatchResultsForUser) => element.rank,
          showCustomPopover: (element: MatchResultsForUser): boolean =>
            !!element.prizeDetails && !!element.eventComplete,
          customElement: (element: MatchResultsForUser) => {
            let winnerElement = '';
            let place = '';
            if (element.eventComplete) {
              const rank =
                element.rank > 0 ? new PlacePipe().transform(element.rank) : '';
              place = `<span>${rank}</span>`;
              if (element.isLifeExperience) {
                const src = element?.prizeDetails?.prizeImageAvatar || '';
                winnerElement =
                  src !== ''
                    ? `
                <img
                class="prize-avatar"
                src="${src}"
                height="48"
                alt="Prize"
                />
                `
                    : '';
              } else {
                const prize = Number(element.awardedPrizeAmount);
                const usdPrize = this.#currencyService.convertDollar(prize);
                winnerElement =
                  prize > 0
                    ? `<div class="prize-ctn primary-color">
                <span class="value">${usdPrize}</span
                > <span class="code">USD</span>
              </div>`
                    : '';
              }
            }
            return `
            <div class="place-ctn">
            ${place}
            ${winnerElement}
            </div>`;
          },
          customPopover: (l: MatchResultsForUser) => {
            let icon = '';
            let label = '';
            let value = '';
            let details = '';
            let classImg = '';
            if (l.eventComplete) {
              l.mobPopoverOpen = false;
              l.deskPopoverOpen = false;
              value = this.#currencyService.convertDollar(
                Number(l.prizeDetails?.prizeValue),
              );
              if (l.isLifeExperience) {
                l.isLEP = true;
                label = l.prizeDetails?.prizeName || '';
                icon =
                  (typeof l.prizeDetails?.prizeImageAvatar === 'string'
                    ? l.prizeDetails?.prizeImageAvatar
                    : l.prizeDetails?.prizeImageAvatar?.url) || '';
                classImg = 'prize-img';
                details = `<div class="details"><span class="details-desc">Market value:</span> <span class="currency mat-body-1">$${value} USD</span>
                </div>`;
              } else {
                label = 'Cash Prize';
                icon = `/shared/svg/cash-prize-icon.svg`;
                details = `<div class="details"><span class="currency mat-body-1">${value} USD</span>
                </div>`;
              }
            }
            const img = `<img src="${icon}" class="${classImg}"/>`;
            return `
            <div class="place-details">
            ${img}
            <p class="mat-subtitle-1 subtitle">${label}</p>
            ${details}
            </div>
           `;
          },
          tooltip: () => null,
        });
      } else if (
        columns[i] === 'playedOn' &&
        this.displayedColumns.findIndex((col) => col.id === 'playedOn') === -1
      ) {
        this.displayedColumns.push({
          id: 'playedOn',
          title: 'Played On',
          type: () => columnTypes.DATE,
          mapping: (element: MatchResultsForUser) => element.datePlayed,
          tooltip: () => null,
        });
      } else if (
        columns[i] === 'actions' &&
        this.displayedColumns.findIndex((col) => col.id === 'actions') === -1
      ) {
        this.displayedColumns.push({
          id: 'actions',
          title: 'Actions',
          type: () => columnTypes.ACTIONS,
          mapping: () => null,
          tooltip: () => null,
          actionsToShow: () => [actionTypes.DETAILS],
        });
      }
    }
  }

  getType(element: MatchResultsForUser): columnTypes {
    if (element.fantazPass) {
      return columnTypes.DEFAULT;
    }
    return columnTypes.CURRENCY;
  }

  elementClicked(cellClicked: IElementModel) {
    if (cellClicked.cellName === 'event') {
      this.viewDetails(cellClicked.data, 'event');
    } else if (cellClicked.cellName === 'event') {
      this.viewDetails(cellClicked.data, 'event');
    } else {
      this.onView(cellClicked.data);
    }
  }

  ngOnInit() {
    if (this.page.pageIndex !== 0 || this.page.pageSize !== 10) {
      this.onPageChange(this.page);
    }
  }

  ngOnDestroy(): void {
    this.gameHistory = null;
    this.totalGameHistory = 0;
    if (this.flexMediaWatcher) {
      this.flexMediaWatcher.unsubscribe();
    }
  }

  ngDoCheck(): void {
    if (this.gameHistory) {
      this.onFilterHistoryChange(this.filterHistoryModel);
    }
    this.setupTable();
  }

  refreshModel(model: RoomFilterModel[]) {
    this.filterHistoryModel = model;
    this.cd.detectChanges();
  }

  onPageChange(page: any) {
    this.pageChange.emit(page);
  }

  onView(element: MatchResultsForUser) {
    if (this.navigateOutside) {
      this.navigateOutsideEvent.emit(
        `/tournaments/gamehistory/view/historydetails/${element.playInstanceUuid}`,
      );
    } else {
      this.router.navigate([`historydetails/${element.playInstanceUuid}`], {
        relativeTo: this.route,
      });
    }
  }

  getGameImage(
    gameKey: string,
    type: 'avatar' | 'card' | 'background' = 'avatar',
  ): string {
    const result = gameKey.split('.');
    const [gameName, version] = result || ['default', 'v1'];

    return `/shared/img/games/${gameName}/${version}/${gameName}-${type}.webp`;
  }

  onTryFree(event: Event) {
    this.tryFree.emit(event);
  }

  onLogin(event: Event) {
    this.login.emit(event);
  }
  onButtonClicked() {
    this.navigateOutsideEvent.emit(`/`);
  }

  trackBy(index: number, item: MatchResultsForUser) {
    return item.playInstanceUuid;
  }

  getRoomNumber(matchResult: MatchResultsForUser) {
    return matchResult.rooms[matchResult.eventScheduleUuidForEventInstance];
  }

  viewDetails(element: MatchResultsForUser, level: string) {
    let url = this.getTournamentUrl(element);
    if (level === 'event' || element.isLifeExperience) {
      url += `/${element.eventScheduleUuidForEventInstance}`;
    }
    if (this.navigateOutside) {
      this.navigateOutsideEvent.emit(`/tournaments/${url}`);
    } else {
      this.router.navigate([url], {});
    }
  }

  private getTournamentUrl(element: MatchResultsForUser): string {
    const prefix = element.isLifeExperience ? 'prizes' : 'cash';
    return `${prefix}/${element.url}`;
  }

  get showFilterButton() {
    return (this.totalGameHistory ?? 0) > 4 && !this.disableFilter;
  }

  onToggleFilter($event: any) {
    this.showFilter = $event;
  }

  onFilterHistoryChange(filters: RoomFilterModel[] | any) {
    let filtersHistorySelected = 0;

    this.gameHistoryFiltered =
      (this.gameHistory &&
        this.gameHistory.filter((event) => {
          let selectedByFilterCount = 0;

          filters.forEach((filter: RoomFilterModel) => {
            if (filter.values) {
              filter.values.forEach((item) => {
                if (item.selected === true) {
                  filtersHistorySelected++;
                  let prize = 0;
                  const entryAmount = event.entryFee;
                  let selected = false;
                  switch (filter.filter) {
                    case 'awardedPrizeAmount':
                      prize = event.awardedPrizeAmount
                        ? event.awardedPrizeAmount
                        : 0;
                      selected = this.isInRange(prize, item);

                      break;
                    case 'entryFee':
                      selected = this.isInRange(entryAmount, item);
                      break;

                    default:
                      break;
                  }
                  if (selected) {
                    selectedByFilterCount++;
                  }
                }
              });
            }
          });
          return selectedByFilterCount > 0;
        })) ||
      [];
    if (filtersHistorySelected == 0) {
      this.gameHistoryFiltered = this.gameHistory as MatchResultsForUser[];
    }
  }

  isInRange(value: number, item: RoomFilter) {
    if (item.type === 'less' && value < item.value.start) {
      return true;
    }

    if (item.type === 'more' && value >= item.value.start) {
      return true;
    }

    if (item.type === 'exact' && value == item.value.start) {
      return true;
    }

    return value >= item.value.start && value <= item.value.end;
  }
}
